import { getLocalFromPrismic } from "@/lib/i18n";
import { getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { Content, isFilled } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import { ChevronRight } from "lucide-react";
import Link from "next/link";

export type NavigationItemProps = SliceComponentProps<Content.NavigationItemSlice> & {
  context?: {
    pathname: string;
  };
};

const NavigationItem = ({ slice, context }: NavigationItemProps) => {
  const { link } = slice.primary;
  const pathname = context?.pathname || "";

  const getUrl = () => {
    if (!isFilled.link(link)) {
      return "#";
    }

    if (link.link_type === "Document") {
      if (link.tags.includes("faq-index")) {
        // If the link is to the FAQ index, return the permalink without UID
        return getPermalink(getLocalFromPrismic(link.lang), "faq");
      }
      return `${getPermalink(getLocalFromPrismic(link.lang), "faq")}/${link.uid}`;
    }
    return link.url;
  };

  const isActive = () => {
    const url = getUrl();
    if (url === "#") return false;

    // For exact matches
    if (pathname === url) return true;

    // For FAQ index page, only be active on exact match or direct /faq path not active on sub-pages like /faq/commandes
    return false;
  };

  const linkIsActive = isActive();

  return (
    <>
      {isFilled.link(link) && (
        <li data-slice-type={slice.slice_type} data-slice-variation={slice.variation}>
          <Link
            href={getUrl()}
            className={cn(
              "group hover:text-dark-primary flex items-center justify-between px-2.5 py-3 font-semibold transition-all duration-200 ease-in-out hover:bg-[#fafaf9] lg:rounded-sm",
              "text-dark-primary focus-visible:bg-pink-primary max-lg:border-b-primary-light/30 focus-visible:text-white-primary active:bg-pink-primary border-b border-transparent active:text-white",
              linkIsActive &&
                "lg:bg-pink-primary max-lg:border-y-pink-primary text-pink-primary bg-festi-pink-50 lg:text-white"
            )}
            aria-current={linkIsActive ? "page" : undefined}
          >
            {link.text}
            <ChevronRight
              className={cn(
                "group-focus-visible:block group-active:block lg:hidden lg:text-white",
                linkIsActive && "max-lg:text-pink-primary lg:block"
              )}
              size={24}
              aria-hidden="true"
            />
          </Link>
        </li>
      )}
    </>
  );
};

export default NavigationItem;
